<template>
  <div class="carousel-widget-container">
    <div class="heading">
      <dashboard-widget-header
        :free-items-count="freeItemsCount"
        :icon="widgetIcon"
        :show-free-counts="showFreeCounts"
        :total-display-name="totalDisplayName"
        :total-items-count="totalItemsCount"
        :title="title"
      />
      <router-link
        v-if="showAllRoute"
        :to="allRoute"
      >
        <hlt-text
          :size="'sm'"
          :variant="'content-secondary'"
        >
          View All
        </hlt-text>
      </router-link>
    </div>
    <carousel :tile-count="tileCount">
      <custom-quiz-tile
        v-if="isCustomQuiz"
        :locked="isLocked()"
        @custom-quiz-tile-clicked="startCustomQuiz"
      />
      <carousel-tile
        v-for="item in items"
        :key="item.id"
        :actions="tileActions(item)"
        :color="color"
        :locked="isLocked(item)"
        :tile-display-name="categoryName(item)"
        :count="categoryCount(item)"
        :badge="isAdaptive(item) ? 'Adaptive' : null"
        :properties="item"
        @action-clicked="actionClicked(item, $event)"
        @tile-clicked="tileClicked(item)"
      />
    </carousel>
  </div>
</template>

<script>
import Carousel from '@components/carousels/carousel.vue';
import CarouselTile from '@components/carousels/carouselTile.vue';
import CustomQuizTile from '@components/ui/customQuizTile.vue';
import DashboardWidgetHeader from '@components/dashboard-widgets/header.vue';
import HltText from '@/components/ui/text.vue';

import DashboardWidgetMixin from '@/mixins/dashboardWidget';
import GlobalMixin from '@/mixins/global';
import QuizMixin from '@/mixins/quizzes';
import SimulationsMixin from '@/mixins/simulations';
import TopicsMixin from '@/mixins/topics';

import { displayName } from '@/utils';
import { PAID_FEATURES } from '@/utils/acl';

export default {
  components: { Carousel, CarouselTile, CustomQuizTile, DashboardWidgetHeader, HltText },
  mixins: [DashboardWidgetMixin, GlobalMixin, QuizMixin, SimulationsMixin, TopicsMixin],
  props: {
    config: {
      type: Object,
    },
  },
  data() {
    return {
      categories: [],
      topics: [],
    };
  },
  computed: {
    allRoute() {
      let name;
      let query;
      let params;

      if (this.isCustomQuiz) {
        name = 'quizzes#index';
        query = { ...this.query, tab: 'my-quizzes' };
      } else if (this.isQuiz) {
        name = 'quizzes#index';
      } else if (this.isTopicsCategories) {
        name = 'topics#index';
      } else if (this.isAttachment) {
        name = 'attachments#index';
      } else if (this.isScenario) {
        name = 'scenarios#index';
        params = {'categoryTypeId': this.categoryType.id};
      } else if (this.isTestSim) {
        name = 'testSimulations#index';
      } else if (this.isTopics) {
        name = 'topics#index';
      } else if (this.isWuxTestSim) {
        name = 'sims#index';
      } else {
        name = 'categories#index';
      }

      return {name: name, params: params, query: query || this.query};
    },
    canAccessCustomQuiz() {
      return this.$access(this.$store, 'feature', PAID_FEATURES.CUSTOM_QUIZZES);
    },
    color() {
      if (this.navigationItem) {
        return this.navigationItem.color || '#00d8f8';
      }
    },
    isAttachment() {
      return this.navigationItem?.destination === 'attachment_categories';
    },
    isCustomQuiz() {
      return this.navigationItem?.destination === 'custom_quiz';
    },
    isQuiz() {
      return this.navigationItem?.destination === 'quiz_categories';
    },
    isTestSim() {
      return this.navigationItem?.destination === 'test_simulations';
    },
    isScenario() {
      return this.navigationItem?.destination === 'scenario_categories';
    },
    isWuxTestSim() {
      return this.navigationItem?.destination === 'wux_test_simulations';
    },
    isTopics() {
      return this.navigationItem?.destination === 'topics';
    },
    isTopicsCategories() {
      return this.navigationItem?.destination === 'topic_categories';
    },
    items() {
      return this.categories.concat(this.topics);
    },
    showAllRoute() {
      return this.items.length > 0 && !this.isWuxTestSim;
    },
    query() {
      if (this.navigationItem) {
        return { nav: this.navigationItem.query };
      }
    },
    tileCount() {
      let extraTile = 0;
      if (this.isCustomQuiz) {
        extraTile = 1;
      }

      return extraTile + this.items.length;
    },
    title() {
      if (this.config) {
        return this.config.name;
      }
    },
    catalogPageFromPurchaseOrder() {
      const purchaseOrderId = this.jsonConfig.purchaseOrderId;
      if (purchaseOrderId) {
        const catalogPages = this.$store.getters['catalog/pageList'];
        return catalogPages.find(page => page.purchaseOrderId === Number(purchaseOrderId));
      }
    },
    jsonConfig() {
      return this.config?.config || {};
    },
  },
  async mounted() {
    if (this.isCustomQuiz) {
      const userQuizCategoryType = this.$store.getters['categoryTypes/userQuizCategoryType'];
      const ids = this.$store.getters['categories/type'][userQuizCategoryType.id] || [];
      this.categories = ids.map(id => this.$store.getters['categories/categories'][id]);
    } else if (this.isTopics) {
      await this.$store.dispatch('topics/load', {category_type_id: this.categoryType.id});
      this.topics = this.categoryTypeTopics(this.categoryType.id);
    } else if (this.categoryType || this.isScenario) {
      const ids = this.$store.getters['categories/type'][this.categoryType.id] || [];
      const categories = ids.map(id => this.$store.getters['categories/categories'][id]);
      this.categories = categories;
    } else {
      this.categories = [];
    }
  },
  methods: {
    actionClicked(item, action) {
      this.$analytics.events.selectedNavItem(this.navigationItem, this.navigationGroup, this.config.widgetType);
      if (action === 'View Results') {
        if (this.isTestSim) {
          this.$router.push({ name: 'testSimulations#results', params: { categoryId: item.id }, query: this.query });
        } else {
          this.$router.push({ name: 'quizzes#results', params: { categoryId: item.id }, query: this.query });
        }
      } else if (action === 'Start Quiz') {
        this.startQuiz(item, this.query);
      } else if (action === 'Continue Quiz') {
        this.continueQuiz(item, this.query);
      } else if (action === 'Restart Quiz') {
        this.restartQuiz(item, this.query);
      }
    },
    categoryCount(item) {
      if (this.isAdaptive(item)) return null;
      if (
          (this.isQuiz || this.isScenario)
          && item.descendants.length > 0
        ) {
        let descendants = item.descendants.map(id => this.$store.getters['categories/categories'][id]);
        let leaves = descendants.filter(d => d.children.length === 0);
        return leaves.length;
      } else {
        return item?.counts?.total;
      }
    },
    categoryName(item) {
      if (this.isAdaptive(item)) return 'Adaptive';
      if (this.isQuiz && item.descendants.length > 0) {
        const count = this.categoryCount(item);
        return displayName(count, 'Quizzes', 'Quiz');
      } else if (this.isTopics) {
        return '';
      } else if (this.categoryType) {
        return displayName(item.counts.total, this.categoryType.displayNamePlural, this.categoryType.displayNameSingular);
      }
    },
    // Adaptive sims have no fixed question count
    isAdaptive(item) {
      return this.$store.getters['adaptiveSim/visibleSimIds'].includes(item?.id);
    },
    isLocked(item) {
      if (this.isCustomQuiz) {
        return !this.canAccessCustomQuiz;
      } else if (this.isTopics) {
        return false;
      } else {
        return !this.$access(this.$store, 'category', item.id);
      }
    },
    startCustomQuiz() {
      this.$router.push({ name: 'quizzes#index', query: this.query });
    },
    tileActions(item) {
      if (this.isCustomQuiz || (this.isQuiz && !this.isLocked(item))) {
        const actions = [];
        const status = this.quizStatus(item);
        const children = this.$store.getters['categories/all'].filter(c => c.parentCategoryId === item.id);

        if (status.name === 'completed') {
          actions.push('View Results');
          if (item.userResettable) {
            actions.push('Restart Quiz');
          }
        } else if (status.name === 'in-progress') {
          actions.push('Continue Quiz');
          if (item.userResettable) {
            actions.push('Restart Quiz');
          }
        } else if (children.length === 0) {
          actions.push('Start Quiz');
        }

        return actions;
      } else if (this.isTestSim && this.finishedSimulation(item)) {
        return ['View Results'];
      }
    },
    topicTileClicked(topic) {
      let categoryId;
      const productAssociations = topic.productAssociations;
      if (productAssociations.length > 1) {
        categoryId = 'all';
      } else {
        categoryId = productAssociations[0].categoryId;
      }

      this.$router.push({name: 'topics#show', params: {topicId: topic.id, categoryId: categoryId}, query: this.query});
    },
    tileClicked(item) {
      const children = this.$store.getters['categories/all'].filter(c => c.parentCategoryId === item.id);
      this.$analytics.events.selectedNavItem(this.navigationItem, this.navigationGroup, this.config.widgetType);
      if (children.length > 0) {
        // go to category page but with parent category
        let routeName;
        let additionalParams;

        if (this.isQuiz) {
          routeName = 'quizzes#index';
        } else if (this.isTopicsCategories) {
          routeName = 'topics#index';
        } else if (this.isAttachment) {
          routeName = 'attachments#index';
        } else if (this.isScenario) {
          routeName = 'scenarios#category';
          additionalParams = {'categoryTypeId': this.categoryType.id};
        } else if (this.isTestSim) {
          routeName = 'testSimulations#index';
        } else if (this.isWuxTestSim && !this.isLocked(item)) {
          routeName = 'sims#index';
        } else if (this.isWuxTestSim && this.isLocked(item)) {
          this.showCatalogModal();
          return;
        } else {
          routeName = 'categories#index';
        }

        this.$router.push({ name: routeName, params: { categoryId: item.id, ...additionalParams }, query: this.query });
      } else if (this.isTopics) {
        this.topicTileClicked(item);
      } else {
        if (this.isLocked(item)) {
          this.$store.dispatch('ui/toggleUpsellModal', {
            show: true,
            resource: { type: 'category', id: item.id },
            source: { type: 'Locked Category', value: item.id },
          });
        } else if (this.isWuxTestSim) {
          return this.$router.push({ name: 'sims#show', params: { simId: item.id }, query: this.query });
        } else {
          let routeName;
          // Go to topic or attachment category
          if (this.isTopicsCategories) {
            routeName = 'topics#index';
          } else if (this.isAttachment) {
            routeName = 'attachments#index';
          } else if (this.isTestSim) {
            const hasFinished = this.finishedSimulation(item);
            if (hasFinished) {
              routeName = 'testSimulations#results';
            } else {
              routeName = 'testSimulations#start';
            }
          } else {
            // start studying in category
            routeName = 'categories#flashcard';
          }

          this.$router.push({ name: routeName, params: { categoryId: item.id }, query: this.query });
        }
      }
    },
    showCatalogModal() {
      this.$store.dispatch('ui/toggleCatalogModal', {
        show: true,
        catalogPageId: this.catalogPageFromPurchaseOrder.id,
        source: {
          type: 'Carousel Widget',
          value: this.config?.id,
        },
      });
    },
  },
};
</script>

<style lang='scss' scoped>
.carousel-widget-container {
  @include padded-widget;

  .heading {
    align-items: baseline;
    display: flex;

    :deep(.dashboard-widget-header) {
      margin-bottom: 0;
    }

    .count {
      color: #b3b2b3;
      font-size: rem-calc(12);
      letter-spacing: 0.5px;
      margin: 0 auto 0 rem-calc(16);
    }
  }
}
</style>
